import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Trash2, Check, AlertTriangle } from "lucide-react";
import MaintenanceLayout from "../shared/MaintenanceLayout";
import { canEditMaintenance } from "../shared/MaintenanceGuard";
import "../shared/maintenance-tables.scss";
import {
  listParties,
  createParty,
  getProfile,
  createProfile,
  updateProfile,
  Party,
  PartyType,
  ProfileLineInput,
  LineKind,
  GstTreatment,
} from "../../../api/settlement";

// ---- styles (maintenance aesthetic) ----------------------------------------
const card: React.CSSProperties = {
  background: "#fff",
  border: "1px solid #e0e0e0",
  borderRadius: 10,
  padding: 16,
  marginBottom: 16,
};

const input: React.CSSProperties = {
  padding: "6px 8px",
  border: "1px solid #ccc",
  borderRadius: 6,
};

const primaryBtn: React.CSSProperties = {
  background: "#2563eb",
  color: "#fff",
  border: "none",
  borderRadius: 8,
  padding: "8px 14px",
  cursor: "pointer",
  fontWeight: 600,
};

const ghostBtn: React.CSSProperties = {
  background: "#fff",
  color: "#2563eb",
  border: "1px solid #2563eb",
  borderRadius: 8,
  padding: "8px 14px",
  cursor: "pointer",
  fontWeight: 600,
};

const th: React.CSSProperties = {
  borderBottom: "1px solid #e0e0e0",
  padding: "8px 10px",
  textAlign: "left",
  fontSize: 12,
  fontWeight: 600,
  color: "#777",
  textTransform: "uppercase",
  letterSpacing: ".03em",
};

const td: React.CSSProperties = {
  borderBottom: "1px solid #f0f0f0",
  padding: "8px 6px",
  color: "#333",
  verticalAlign: "middle",
};

// Editable line row in local form state. rate is kept as a string so the input
// can be cleared/typed freely; parsed to a number only for math & submit.
interface LineRow {
  line_kind: LineKind;
  party_id: number | "";
  label: string;
  rate: string;
  gst_treatment: GstTreatment;
}

const GST_OPTIONS: { value: GstTreatment; label: string }[] = [
  { value: "exempt", label: "Exempt" },
  { value: "gst_18_forward", label: "18% · forward" },
  { value: "gst_18_rcm", label: "18% · RCM" },
];

const num = (s: string): number => {
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
};

const RateCardEditor: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const canEdit = canEditMaintenance();

  const pricingOverrideIdRaw = searchParams.get("pricing_override_id");
  const profileIdRaw = searchParams.get("profile_id");
  const taxablePrefillRaw = searchParams.get("taxable");
  const gstPctRaw = searchParams.get("gst_pct");
  const taxModelRaw = searchParams.get("tax_model");
  const gstPct = gstPctRaw != null && gstPctRaw !== "" ? Number(gstPctRaw) : null;
  const taxModel: "inclusive" | "exclusive" | null =
    taxModelRaw === "inclusive"
      ? "inclusive"
      : taxModelRaw === "exclusive"
      ? "exclusive"
      : null;
  const orgIdRaw = searchParams.get("org_id");
  const orgId = orgIdRaw != null && orgIdRaw !== "" ? Number(orgIdRaw) : null;

  const pricingOverrideId =
    pricingOverrideIdRaw != null && pricingOverrideIdRaw !== ""
      ? Number(pricingOverrideIdRaw)
      : NaN;
  const profileId =
    profileIdRaw != null && profileIdRaw !== "" ? Number(profileIdRaw) : null;
  const isEdit = profileId != null && !Number.isNaN(profileId);

  const [parties, setParties] = useState<Party[]>([]);
  const [taxablePrice, setTaxablePrice] = useState<string>(
    taxablePrefillRaw != null && taxablePrefillRaw !== "" ? taxablePrefillRaw : ""
  );
  const [lines, setLines] = useState<LineRow[]>([]);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  // inline "create party" — so you don't have to leave the editor to add a host/consultant
  const [showNewParty, setShowNewParty] = useState(false);
  const [npName, setNpName] = useState("");
  const [npType, setNpType] = useState<PartyType>("host");
  const [npScope, setNpScope] = useState<"org" | "global">(orgId != null ? "org" : "global");
  const [npSaving, setNpSaving] = useState(false);
  const [npError, setNpError] = useState("");

  const handleCreateParty = async () => {
    if (!npName.trim()) {
      setNpError("Name is required.");
      return;
    }
    setNpSaving(true);
    setNpError("");
    try {
      const created = await createParty({
        name: npName.trim(),
        party_type: npType,
        gst_registration_status: "unregistered",
        org_id: npScope === "org" ? orgId : null,
      });
      setParties((prev) => [...prev, created]);
      setNpName("");
      setNpType("host");
      setShowNewParty(false);
    } catch (err: any) {
      setNpError(err?.response?.data?.detail || "Failed to create party.");
    } finally {
      setNpSaving(false);
    }
  };

  // ---- load parties + (edit-mode) existing profile -------------------------
  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError("");

    if (Number.isNaN(pricingOverrideId)) {
      setLoading(false);
      setLoadError("Missing or invalid pricing_override_id.");
      return () => {
        active = false;
      };
    }

    const partiesP = listParties(orgId ?? undefined);
    const profileP =
      isEdit && profileId != null ? getProfile(profileId) : Promise.resolve(null);

    Promise.all([partiesP, profileP])
      .then(([partyList, profile]) => {
        if (!active) return;
        setParties(partyList);
        if (profile) {
          setTaxablePrice(String(profile.taxable_unit_price));
          setLines(
            profile.lines.map((l) => ({
              line_kind: l.line_kind,
              party_id: l.party_id,
              label: l.label,
              rate: String(l.rate_per_kwh),
              gst_treatment: l.gst_treatment ?? "exempt",
            }))
          );
        }
      })
      .catch((err) => {
        console.error("Failed to load rate card editor:", err);
        if (active) setLoadError("Failed to load parties or profile.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pricingOverrideId, profileId, isEdit]);

  // ---- live balance invariant ----------------------------------------------
  const sum = useMemo(
    () => lines.reduce((acc, l) => acc + num(l.rate), 0),
    [lines]
  );
  const taxable = num(taxablePrice);
  const diff = sum - taxable;
  const balanced = Math.abs(diff) <= 0.01;

  // GST overlay for the banner. Canonical rule (ch-adda-api calculate_tax_breakdown):
  // tax = taxable × pct, customer pays = taxable + tax — identical math for BOTH models.
  // Only the narrative differs: exclusive adds GST on top of the configured price,
  // inclusive carves it out of the configured price.
  const gstAmt = gstPct ? taxable * (gstPct / 100) : 0;
  const customerPays = taxable + gstAmt;

  // ---- row mutators --------------------------------------------------------
  const addLine = (kind: LineKind) => {
    setLines((prev) => [
      ...prev,
      { line_kind: kind, party_id: "", label: "", rate: "", gst_treatment: "exempt" },
    ]);
  };

  const removeLine = (idx: number) => {
    setLines((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateLine = <K extends keyof LineRow>(
    idx: number,
    key: K,
    value: LineRow[K]
  ) => {
    setLines((prev) =>
      prev.map((l, i) => (i === idx ? { ...l, [key]: value } : l))
    );
  };

  // ---- save ----------------------------------------------------------------
  const allPartiesChosen = lines.every((l) => l.party_id !== "");
  const canSave =
    canEdit && balanced && lines.length > 0 && allPartiesChosen && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setSaveError("");

    const payloadLines: ProfileLineInput[] = lines.map((l, i) => ({
      line_kind: l.line_kind,
      party_id: l.party_id as number,
      label: l.label,
      rate_per_kwh: num(l.rate),
      gst_treatment: l.gst_treatment,
      sort_order: i,
    }));

    try {
      if (isEdit && profileId != null) {
        await updateProfile(profileId, {
          taxable_unit_price: taxable,
          lines: payloadLines,
        });
      } else {
        await createProfile({
          pricing_override_id: pricingOverrideId,
          taxable_unit_price: taxable,
          lines: payloadLines,
        });
      }
      navigate(-1);
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      setSaveError(
        typeof detail === "string"
          ? detail
          : "Failed to save profile. Please review the lines and try again."
      );
      setSaving(false);
    }
  };

  // ---- render --------------------------------------------------------------
  if (loading) {
    return (
      <MaintenanceLayout title="Settlement · Rate card">
        <p style={{ color: "#777", margin: 0 }}>Loading…</p>
      </MaintenanceLayout>
    );
  }

  if (loadError) {
    return (
      <MaintenanceLayout title="Settlement · Rate card">
        <div style={card}>
          <p style={{ color: "#c0392b", margin: 0 }}>{loadError}</p>
        </div>
      </MaintenanceLayout>
    );
  }

  return (
    <MaintenanceLayout title="Settlement · Rate card">
      {!canEdit && (
        <div
          style={{
            ...card,
            background: "#fff7e6",
            border: "1px solid #f0d39a",
            color: "#b9770e",
          }}
        >
          You have read-only access. Editing the rate card requires super-admin.
        </div>
      )}

      {/* GST breakdown — exclusive adds GST on top of the configured price;
          inclusive carves it out of the configured price. */}
      {taxModel != null && (
        <div style={{ ...card, background: "#eef4ff", border: "1px solid #cfe0ff", color: "#1e3a8a" }}>
          <div style={{ fontSize: 13 }}>
            {taxModel === "exclusive" ? (
              <>
                <b>Exclusive</b> rate — taxable <b>₹{taxable.toFixed(2)}/kWh</b>
                {gstPct ? (
                  <>
                    {" "}
                    + ₹{gstAmt.toFixed(2)} GST ({gstPct}%, added on top)
                  </>
                ) : null}{" "}
                → customer pays <b>₹{customerPays.toFixed(2)}/kWh</b>.
              </>
            ) : (
              <>
                <b>Inclusive</b> rate — customer pays <b>₹{customerPays.toFixed(2)}/kWh</b>
                {gstPct ? <> (incl. {gstPct}% GST)</> : null} → <b>₹{taxable.toFixed(2)} taxable</b>
                {gstPct ? <> + ₹{gstAmt.toFixed(2)} GST</> : null}.
              </>
            )}{" "}
            The rate card splits the <b>taxable</b> base only; GST is remitted separately.
          </div>
        </div>
      )}

      {/* Taxable unit price */}
      <div style={card}>
        <label
          style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#333" }}
        >
          Taxable unit price (₹/kWh)
          <div style={{ marginTop: 6 }}>
            <input
              type="number"
              step="0.01"
              min="0"
              value={taxablePrice}
              onChange={(e) => setTaxablePrice(e.target.value)}
              disabled={!canEdit}
              style={{ ...input, width: 180 }}
            />
          </div>
        </label>
        <p style={{ margin: "8px 0 0", fontSize: 13, color: "#777" }}>
          Pre-GST per-kWh price. Every line below must sum exactly to this figure.
        </p>
      </div>

      {/* Balance bar */}
      <div
        style={{
          ...card,
          display: "flex",
          alignItems: "center",
          gap: 10,
          background: balanced ? "#e8f7ef" : "#fdecea",
          border: `1px solid ${balanced ? "#9fd9b8" : "#f0b1ab"}`,
          color: balanced ? "#1a7f4b" : "#c0392b",
          fontWeight: 700,
          fontSize: 15,
        }}
      >
        {balanced ? <Check size={18} /> : <AlertTriangle size={18} />}
        {balanced ? (
          <span>Balanced (₹{sum.toFixed(2)})</span>
        ) : (
          <span>
            Off by ₹{diff.toFixed(2)} — lines must sum to the unit price
          </span>
        )}
      </div>

      {/* Lines */}
      <div style={card}>
        <h3 style={{ margin: "0 0 12px", fontSize: 16, fontWeight: 600, color: "#333" }}>
          Lines
        </h3>

        {lines.length === 0 ? (
          <p style={{ color: "#777", margin: "0 0 12px" }}>
            No lines yet. Add a party share or a consumable/cost to begin.
          </p>
        ) : (
          <table className="maint-stack" style={{ borderCollapse: "collapse", width: "100%", fontSize: 14 }}>
            <thead>
              <tr>
                <th style={th}>Kind</th>
                <th style={th}>Party</th>
                <th style={th}>Label</th>
                <th style={{ ...th, textAlign: "right" }}>₹/kWh</th>
                <th style={th}>GST treatment</th>
                <th style={th} />
              </tr>
            </thead>
            <tbody>
              {lines.map((l, idx) => (
                <tr key={idx}>
                  <td data-label="Kind" style={td}>
                    <select
                      value={l.line_kind}
                      onChange={(e) =>
                        updateLine(idx, "line_kind", e.target.value as LineKind)
                      }
                      disabled={!canEdit}
                      style={input}
                    >
                      <option value="share">Party share</option>
                      <option value="consumable">Consumable / cost</option>
                    </select>
                  </td>
                  <td data-label="Party" style={td}>
                    <select
                      value={l.party_id === "" ? "" : String(l.party_id)}
                      onChange={(e) =>
                        updateLine(
                          idx,
                          "party_id",
                          e.target.value === "" ? "" : Number(e.target.value)
                        )
                      }
                      disabled={!canEdit}
                      style={{ ...input, minWidth: 160 }}
                    >
                      <option value="">Select party…</option>
                      {parties.some((p) => p.org_id == null) && (
                        <optgroup label="Global / shared">
                          {parties
                            .filter((p) => p.org_id == null)
                            .map((p) => (
                              <option key={p.id} value={p.id}>
                                {p.name}
                              </option>
                            ))}
                        </optgroup>
                      )}
                      {parties.some((p) => p.org_id != null) && (
                        <optgroup label="This organisation">
                          {parties
                            .filter((p) => p.org_id != null)
                            .map((p) => (
                              <option key={p.id} value={p.id}>
                                {p.name}
                              </option>
                            ))}
                        </optgroup>
                      )}
                    </select>
                  </td>
                  <td data-label="Label" style={td}>
                    <input
                      type="text"
                      value={l.label}
                      onChange={(e) => updateLine(idx, "label", e.target.value)}
                      disabled={!canEdit}
                      placeholder="e.g. Host rent share"
                      style={{ ...input, minWidth: 160 }}
                    />
                  </td>
                  <td data-label="₹/kWh" style={{ ...td, textAlign: "right" }}>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={l.rate}
                      onChange={(e) => updateLine(idx, "rate", e.target.value)}
                      disabled={!canEdit}
                      style={{ ...input, width: 90, textAlign: "right" }}
                    />
                  </td>
                  <td data-label="GST treatment" style={td}>
                    <select
                      value={l.gst_treatment}
                      onChange={(e) =>
                        updateLine(
                          idx,
                          "gst_treatment",
                          e.target.value as GstTreatment
                        )
                      }
                      disabled={!canEdit}
                      style={input}
                    >
                      {GST_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td style={{ ...td, textAlign: "center" }}>
                    <button
                      type="button"
                      onClick={() => removeLine(idx)}
                      disabled={!canEdit}
                      title="Remove line"
                      style={{
                        background: "none",
                        border: "none",
                        cursor: canEdit ? "pointer" : "not-allowed",
                        color: "#c0392b",
                        padding: 4,
                        display: "inline-flex",
                        opacity: canEdit ? 1 : 0.5,
                      }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {canEdit && (
          <div style={{ display: "flex", gap: 10, marginTop: 12, flexWrap: "wrap" }}>
            <button type="button" onClick={() => addLine("share")} style={ghostBtn}>
              <Plus size={14} style={{ verticalAlign: "-2px", marginRight: 4 }} />
              Add party share
            </button>
            <button type="button" onClick={() => addLine("consumable")} style={ghostBtn}>
              <Plus size={14} style={{ verticalAlign: "-2px", marginRight: 4 }} />
              Add consumable/cost
            </button>
            <button type="button" onClick={() => setShowNewParty((s) => !s)} style={ghostBtn}>
              <Plus size={14} style={{ verticalAlign: "-2px", marginRight: 4 }} />
              New party
            </button>
          </div>
        )}

        {canEdit && parties.length <= 1 && (
          <p style={{ margin: "10px 0 0", fontSize: 13, color: "#b9770e" }}>
            Only ChargingAdda exists so far — add the host/RWA, consultant, or other parties via
            “New party” (or the Parties page) before building the split.
          </p>
        )}

        {canEdit && showNewParty && (
          <div
            style={{
              marginTop: 12,
              padding: 12,
              border: "1px dashed #c9c9c9",
              borderRadius: 8,
              background: "#fafafa",
              display: "flex",
              gap: 10,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <input
              type="text"
              value={npName}
              onChange={(e) => setNpName(e.target.value)}
              placeholder="Party name (e.g. Green Valley RWA)"
              style={{ ...input, minWidth: 220 }}
            />
            <select
              value={npType}
              onChange={(e) => setNpType(e.target.value as PartyType)}
              style={input}
            >
              <option value="host">Host / operator</option>
              <option value="consultant">Consultant</option>
              <option value="platform">Platform</option>
              <option value="custom">Custom</option>
            </select>
            {orgId != null && (
              <select
                value={npScope}
                onChange={(e) => setNpScope(e.target.value as "org" | "global")}
                style={input}
                title="Org-level parties (e.g. this org's host) are only offered on this org's rate cards"
              >
                <option value="org">This organisation</option>
                <option value="global">Global (shared)</option>
              </select>
            )}
            <button
              type="button"
              onClick={handleCreateParty}
              disabled={npSaving}
              style={{ ...primaryBtn, ...(npSaving ? { opacity: 0.5 } : {}) }}
            >
              {npSaving ? "Saving…" : "Save party"}
            </button>
            <button type="button" onClick={() => setShowNewParty(false)} style={ghostBtn}>
              Cancel
            </button>
            {npError && <span style={{ fontSize: 13, color: "#c0392b" }}>{npError}</span>}
          </div>
        )}
      </div>

      {/* Save */}
      <div style={card}>
        {saveError && (
          <p style={{ color: "#c0392b", margin: "0 0 10px" }}>{saveError}</p>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            style={{
              ...primaryBtn,
              ...(canSave
                ? {}
                : { opacity: 0.5, cursor: "not-allowed" }),
            }}
          >
            {saving ? "Saving…" : isEdit ? "Save profile" : "Save profile"}
          </button>
          <button type="button" onClick={() => navigate(-1)} style={ghostBtn}>
            Cancel
          </button>
          {!balanced && lines.length > 0 && (
            <span style={{ fontSize: 13, color: "#c0392b" }}>
              Balance the lines to enable saving.
            </span>
          )}
          {balanced && lines.length === 0 && (
            <span style={{ fontSize: 13, color: "#777" }}>
              Add at least one line.
            </span>
          )}
          {balanced && lines.length > 0 && !allPartiesChosen && (
            <span style={{ fontSize: 13, color: "#c0392b" }}>
              Select a party on every line.
            </span>
          )}
        </div>
      </div>
    </MaintenanceLayout>
  );
};

export default RateCardEditor;
